/**
 * Aircrafts Registry Hooks (v2.1)
 *
 * Unified merge-and-classify pattern:
 * 1. Fetch all org aircraft (base dataset)
 * 2. Fetch on-field allocations (presence enrichment)
 * 3. Fetch based aircraft data (relationship enrichment)
 * 4. Fetch active transient stays (relationship enrichment)
 * 5. Merge into unified AircraftRegistryRow[] with computed presence/relationship/crm_stage
 * 6. Apply quick filters client-side
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import { supabase } from '@/lib/supabase'
import type { AircraftRegistryRow, AircraftRegistrySummary, OnFieldStatus, SavedViewId, QuickFilterState, PresenceFilter, CrmStageFilter, AircraftRelationship } from '@/types/aircrafts'
import { computeAnomalyFlags } from '@/types/aircrafts'
import type { RecordStatusFilter } from '@/types/record-status'
import { applyRecordStatusFilter } from '@/lib/api/helpers/apply-record-status-filter'

// ============================================
// Query Keys
// ============================================

export const aircraftRegistryKeys = {
  all: ['aircraft-registry'] as const,
  onField: (organizationId: string, locationId: string) =>
    [...aircraftRegistryKeys.all, 'on-field', organizationId, locationId] as const,
  based: (organizationId: string) =>
    [...aircraftRegistryKeys.all, 'based', organizationId] as const,
  allOrg: (organizationId: string, recordStatus: RecordStatusFilter = 'active') =>
    [...aircraftRegistryKeys.all, 'all-org', organizationId, recordStatus] as const,
  transients: (organizationId: string, locationId: string) =>
    [...aircraftRegistryKeys.all, 'transients', organizationId, locationId] as const,
}

// ============================================
// Helper Functions
// ============================================

function getClientDisplayName(client: {
  company_name: string | null
  first_name: string | null
  last_name: string | null
} | null): string | null {
  if (!client) return null
  if (client.company_name) return client.company_name
  if (client.first_name || client.last_name) {
    return [client.first_name, client.last_name].filter(Boolean).join(' ')
  }
  return null
}

function computeOnFieldStatus(row: {
  arrived_at: string | null
  scheduled_departure: string | null
  position_type: string | null
}): OnFieldStatus {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)

  if (row.arrived_at) {
    const arrivedDate = new Date(row.arrived_at)
    if (arrivedDate >= today && arrivedDate < tomorrow) {
      if (arrivedDate > now) {
        return 'arriving'
      }
    }
  }

  if (row.scheduled_departure) {
    const departDate = new Date(row.scheduled_departure)
    if (departDate >= today && departDate < tomorrow) {
      return 'departing'
    }
  }

  if (row.position_type === 'hangar') {
    return 'in_hangar'
  }

  return 'on_ramp'
}

// ============================================
// Based Aircraft Data (lease contracts)
// ============================================

interface BasedAircraftInfo {
  lease_contract_id: string
  contract_number: string
  end_date: string | null
  monthly_rate: number
  contract_type: string
}

function useBasedAircraftData() {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: aircraftRegistryKeys.based(organizationId || ''),
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<Map<string, BasedAircraftInfo>> => {
      const { data, error } = await supabase
        .from('contract_aircraft')
        .select(`
          aircraft_id,
          lease_contract_id,
          lease_contract:lease_contract_id (
            id,
            contract_number,
            end_date,
            monthly_rate,
            status,
            type
          )
        `)
        .is('removed_at', null)

      if (error) throw error

      const map = new Map<string, BasedAircraftInfo>()
      for (const row of data ?? []) {
        const contract = row.lease_contract as unknown as {
          id: string
          contract_number: string
          end_date: string | null
          monthly_rate: number
          status: string
          type: string
        } | null

        if (!contract) continue
        // Only include active/expiring leases
        if (!['active', 'expiring'].includes(contract.status)) continue

        map.set(row.aircraft_id, {
          lease_contract_id: contract.id,
          contract_number: contract.contract_number,
          end_date: contract.end_date,
          monthly_rate: contract.monthly_rate,
          contract_type: contract.type,
        })
      }
      return map
    },
    enabled: !!organizationId,
  })
}

// ============================================
// Active Transient Stays
// ============================================

interface TransientStayInfo {
  transient_stay_id: string
  booking_number: string
  check_in: string
  expected_check_out: string
}

function useActiveTransientStays(locationId: string | null) {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: aircraftRegistryKeys.transients(organizationId || '', locationId || ''),
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<Map<string, TransientStayInfo>> => {
      const { data, error } = await supabase
        .from('transient_stays')
        .select(`
          id,
          aircraft_id,
          booking_number,
          check_in,
          expected_check_out,
          status
        `)
        .eq('location_id', locationId!)
        .in('status', ['reserved', 'confirmed', 'checked_in'])
        .is('deleted_at', null)

      if (error) throw error

      const map = new Map<string, TransientStayInfo>()
      for (const row of data ?? []) {
        map.set(row.aircraft_id, {
          transient_stay_id: row.id,
          booking_number: row.booking_number,
          check_in: row.check_in,
          expected_check_out: row.expected_check_out,
        })
      }
      return map
    },
    enabled: !!organizationId && !!locationId,
  })
}

// ============================================
// On Field Aircraft (allocations)
// ============================================

interface OnFieldInfo {
  allocation_id: string
  position_type: string | null
  building_name: string | null
  unit_name: string | null
  arrived_at: string | null
  on_field_status: OnFieldStatus
  days_on_field: number | null
}

function useOnFieldAircraft(locationId: string | null) {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: aircraftRegistryKeys.onField(organizationId || '', locationId || ''),
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<Map<string, OnFieldInfo>> => {
      const { data, error } = await supabase
        .from('allocations')
        .select(`
          id,
          aircraft_id,
          position_type,
          starts_at,
          area:area_id (name),
          unit:unit_id (name)
        `)
        .eq('location_id', locationId!)
        .eq('status', 'active')
        .in('presence', ['present', 'away'])
        .is('deleted_at', null)

      if (error) throw error

      const map = new Map<string, OnFieldInfo>()
      for (const alloc of data ?? []) {
        const area = alloc.area as unknown as { name: string } | null
        const unit = alloc.unit as unknown as { name: string } | null
        const arrivedAt = alloc.starts_at
        const daysOnField = arrivedAt
          ? Math.ceil((Date.now() - new Date(arrivedAt).getTime()) / (1000 * 60 * 60 * 24))
          : null

        const info: OnFieldInfo = {
          allocation_id: alloc.id,
          position_type: alloc.position_type,
          building_name: area?.name ?? null,
          unit_name: unit?.name ?? null,
          arrived_at: arrivedAt,
          on_field_status: computeOnFieldStatus({
            arrived_at: arrivedAt,
            scheduled_departure: null, // enriched later from transient stays
            position_type: alloc.position_type,
          }),
          days_on_field: daysOnField,
        }
        map.set(alloc.aircraft_id, info)
      }
      return map
    },
    enabled: !!organizationId && !!locationId,
  })
}

// ============================================
// All Org Aircraft (base dataset)
// ============================================

function useAllOrgAircraft(recordStatus: RecordStatusFilter = 'active') {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: aircraftRegistryKeys.allOrg(organizationId || '', recordStatus),
    refetchOnWindowFocus: true,
    queryFn: async () => {
      let query = supabase
        .from('aircraft')
        .select(`
          id,
          organization_id,
          tail_number,
          aircraft_type_id,
          manufacturer,
          model,
          owner_client_id,
          operator_client_id,
          owner_name,
          length,
          wingspan,
          height,
          tail_height,
          empty_weight,
          max_takeoff_weight,
          registration_country,
          serial_number,
          year_manufactured,
          annual_inspection_due,
          insurance_expiry,
          home_location_id,
          current_location_id,
          current_area_id,
          custom_model_file_path,
          custom_model_scale,
          status,
          notes,
          settings,
          created_at,
          updated_at,
          deleted_at,
          aircraft_type:aircraft_type_id (
            id,
            type_code,
            manufacturer,
            model,
            category
          ),
          owner_client:owner_client_id (
            id,
            company_name,
            first_name,
            last_name
          ),
          operator_client:operator_client_id (
            id,
            company_name,
            first_name,
            last_name
          ),
          home_location:home_location_id (
            id,
            name,
            location_code
          )
        `)
        .eq('organization_id', organizationId!)

      query = applyRecordStatusFilter(query, recordStatus)

      const { data, error } = await query.order('tail_number')

      if (error) throw error
      return data ?? []
    },
    enabled: !!organizationId,
  })
}

// ============================================
// Quick Filter Application
// ============================================

function applyQuickFilters(
  data: AircraftRegistryRow[],
  filters: QuickFilterState
): AircraftRegistryRow[] {
  return data.filter((row) => {
    // Presence filter (empty = show all)
    if (filters.presence.length > 0) {
      if (!filters.presence.includes(row.presence)) return false
    }

    // Relationship filter (empty = show all)
    if (filters.relationship.length > 0) {
      const match = filters.relationship.some((f) => {
        if (f === 'based') return row.relationship === 'based'
        if (f === 'transient') return row.relationship === 'transient'
        if (f === 'no_contract') return row.relationship === 'unknown' && row.allocation_id !== null
        return false
      })
      if (!match) return false
    }

    // CRM Stage filter (empty = show all)
    if (filters.stage.length > 0) {
      if (!filters.stage.includes(row.crm_stage)) return false
    }

    // Location filter (empty = show all)
    if (filters.location.length > 0) {
      const rowLocation = row.home_location_name ?? ''
      if (!filters.location.includes(rowLocation)) return false
    }

    return true
  })
}

// ============================================
// Unified Registry Hook
// ============================================

export function useAircraftRegistry(
  _activeView: SavedViewId,
  quickFilters: QuickFilterState,
  locationId: string | null,
  recordStatus: RecordStatusFilter = 'active'
) {
  const allOrgQuery = useAllOrgAircraft(recordStatus)
  const onFieldQuery = useOnFieldAircraft(locationId)
  const basedQuery = useBasedAircraftData()
  const transientsQuery = useActiveTransientStays(locationId)

  // Merge all data sources into unified AircraftRegistryRow[]
  const mergedData = useMemo((): AircraftRegistryRow[] => {
    const allAircraft = allOrgQuery.data
    if (!allAircraft) return []

    const onFieldMap = onFieldQuery.data ?? new Map()
    const basedMap = basedQuery.data ?? new Map()
    const transientMap = transientsQuery.data ?? new Map()

    return allAircraft.map((a): AircraftRegistryRow => {
      const aircraftType = a.aircraft_type as unknown as { id: string; type_code: string; manufacturer: string | null; model: string | null; category: string | null } | null
      const ownerClient = a.owner_client as unknown as { id: string; company_name: string | null; first_name: string | null; last_name: string | null } | null
      const operatorClient = a.operator_client as unknown as { id: string; company_name: string | null; first_name: string | null; last_name: string | null } | null
      const homeLocation = a.home_location as unknown as { id: string; name: string; location_code: string } | null

      // Get enrichment data
      const onFieldInfo = onFieldMap.get(a.id)
      const basedInfo = basedMap.get(a.id)
      const transientInfo = transientMap.get(a.id)

      // Compute relationship
      let relationship: AircraftRelationship = 'unknown'
      if (basedInfo) {
        relationship = 'based'
      } else if (transientInfo) {
        relationship = 'transient'
      }

      // Compute presence
      let presence: PresenceFilter = 'away'
      if (onFieldInfo) {
        if (onFieldInfo.on_field_status === 'arriving') {
          presence = 'arriving'
        } else if (onFieldInfo.on_field_status === 'departing') {
          presence = 'departing'
        } else {
          presence = 'on_field'
        }
      }

      // Compute CRM stage
      let crm_stage: CrmStageFilter = 'unknown'
      if (basedInfo || transientInfo) {
        crm_stage = 'active'
      }
      // Former: had relationship implied by lease expired — not yet tracked, leave unknown

      // Update on_field_status with transient departure info
      let scheduled_departure: string | null = null
      let on_field_status = onFieldInfo?.on_field_status ?? null
      if (transientInfo && onFieldInfo) {
        scheduled_departure = transientInfo.expected_check_out
        // Recompute with departure info
        on_field_status = computeOnFieldStatus({
          arrived_at: onFieldInfo.arrived_at,
          scheduled_departure,
          position_type: onFieldInfo.position_type,
        })
        // Update presence from recomputed status
        if (on_field_status === 'departing') presence = 'departing'
        if (on_field_status === 'arriving') presence = 'arriving'
      }

      const row: AircraftRegistryRow = {
        // Base aircraft fields
        id: a.id,
        organization_id: a.organization_id,
        tail_number: a.tail_number,
        aircraft_type_id: a.aircraft_type_id,
        aircraft_type_name: aircraftType?.type_code ?? null,
        aircraft_type_manufacturer: aircraftType?.manufacturer ?? null,
        aircraft_type_model: aircraftType?.model ?? null,
        aircraft_type_category: aircraftType?.category ?? null,
        manufacturer: a.manufacturer,
        model: a.model,
        owner_client_id: a.owner_client_id,
        owner_client_name: getClientDisplayName(ownerClient),
        operator_client_id: a.operator_client_id,
        operator_client_name: getClientDisplayName(operatorClient),
        owner_name: a.owner_name,
        length: a.length,
        wingspan: a.wingspan,
        height: a.height,
        tail_height: a.tail_height,
        empty_weight: a.empty_weight,
        max_takeoff_weight: a.max_takeoff_weight,
        registration_country: a.registration_country,
        serial_number: a.serial_number,
        year_manufactured: a.year_manufactured,
        annual_inspection_due: a.annual_inspection_due,
        insurance_expiry: a.insurance_expiry,
        home_location_id: a.home_location_id,
        home_location_name: homeLocation?.name ?? null,
        current_location_id: a.current_location_id,
        current_location_name: null,
        current_area_id: a.current_area_id,
        current_area_name: null,
        custom_model_file_path: a.custom_model_file_path,
        custom_model_scale: a.custom_model_scale,
        status: a.status as 'active' | 'maintenance' | 'storage' | 'departed' | 'inactive',
        notes: a.notes,
        settings: a.settings as Record<string, unknown> | null,
        created_at: a.created_at,
        updated_at: a.updated_at,
        deleted_at: a.deleted_at,

        // Position data from allocation
        allocation_id: onFieldInfo?.allocation_id ?? null,
        position_type: onFieldInfo?.position_type ?? null,
        building_name: onFieldInfo?.building_name ?? null,
        unit_name: onFieldInfo?.unit_name ?? null,
        arrived_at: onFieldInfo?.arrived_at ?? null,
        scheduled_departure,
        on_field_status,
        days_on_field: onFieldInfo?.days_on_field ?? null,

        // Presence & CRM stage (computed)
        presence,
        crm_stage,

        // Relationship data
        relationship,
        lease_contract_id: basedInfo?.lease_contract_id ?? null,
        lease_contract_number: basedInfo?.contract_number ?? null,
        lease_end_date: basedInfo?.end_date ?? null,
        lease_monthly_rate: basedInfo?.monthly_rate ?? null,
        transient_stay_id: transientInfo?.transient_stay_id ?? null,
        transient_booking_number: transientInfo?.booking_number ?? null,

        // Business metrics (Phase 2+)
        revenue_mtd: null,
        revenue_ytd: null,
        ar_balance: null,

        // Activity metrics (Phase 3)
        visits_12mo: null,
        avg_stay_days: null,
        last_visit_date: null,
        fuel_purchased_ytd: null,

        // Anomaly flags (computed below)
        anomalies: [],
      }

      row.anomalies = computeAnomalyFlags(row)
      return row
    })
  }, [allOrgQuery.data, onFieldQuery.data, basedQuery.data, transientsQuery.data])

  // Apply quick filters
  const filteredData = useMemo(
    () => applyQuickFilters(mergedData, quickFilters),
    [mergedData, quickFilters]
  )

  // Compute summary
  const summary = useMemo((): AircraftRegistrySummary => {
    const data = filteredData

    const inHangarCount = data.filter((r) => r.on_field_status === 'in_hangar').length
    const onRampCount = data.filter((r) => r.on_field_status === 'on_ramp').length
    const arrivingCount = data.filter((r) => r.presence === 'arriving').length
    const departingCount = data.filter((r) => r.presence === 'departing').length
    const onFieldCount = data.filter((r) => r.presence !== 'away').length
    const basedCount = data.filter((r) => r.relationship === 'based').length
    const transientCount = data.filter((r) => r.relationship === 'transient').length
    const noContractCount = data.filter((r) => r.relationship === 'unknown' && r.allocation_id !== null).length
    const awayCount = data.filter((r) => r.presence === 'away').length

    return {
      totalAircraft: data.length,
      inHangarCount,
      onRampCount,
      arrivingCount,
      departingCount,
      hangarCapacityPercent: 0,
      rampCapacityPercent: 0,
      basedCount,
      transientCount,
      noContractCount,
      awayCount,
      onFieldCount,
    }
  }, [filteredData])

  // Per-view unfiltered counts (for tab badges)
  const viewCounts = useMemo(() => {
    const all = mergedData
    return {
      ramp: all.filter((r) => r.presence !== 'away').length,
      based_fleet: all.filter((r) => r.relationship === 'based').length,
      transients: all.filter((r) => r.relationship === 'transient' || (r.relationship === 'unknown' && r.allocation_id !== null)).length,
      all: all.length,
    }
  }, [mergedData])

  const isLoading = allOrgQuery.isLoading || onFieldQuery.isLoading || basedQuery.isLoading || transientsQuery.isLoading

  return {
    data: filteredData,
    allData: mergedData,
    isLoading,
    isError: allOrgQuery.isError || basedQuery.isError,
    error: allOrgQuery.error || basedQuery.error,
    refetch: allOrgQuery.refetch,
    summary,
    viewCounts,
  }
}

// Keep legacy export for backwards compatibility with other consumers
export { useAllOrgAircraft as useAllKnownAircraft }
